import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  matVisibility,
  matVisibilityOff,
} from '@ng-icons/material-icons/baseline';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  input,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { AuthenService } from '@core/authen/authen.service';
import {
  FormComponentInterface,
  FormErrorService,
} from '@core/form/form-error.service';
import { EventPaymentService } from '@core/http/event-payment.service';
import { SessionService } from '@core/session.service';
import { environment } from '@env/environment';
import { TranslateModule } from '@ngx-translate/core';
import { LoginWithLinkedinBtnComponent } from '@shared/components/login-with-linkedin-btn/login-with-linkedin-btn.component';
import { DebounceClickDirective } from '@shared/directives/debounce-click.directive';
import { LanguagePathDirective } from '@shared/directives/language-path.directive';
import { SessionKeyEnum } from '@shared/enums/session.key.enum';
import { UrlParam } from '@shared/enums/url-param.enum';
import { EventInterface } from '@shared/interfaces/event.interface';
import { LoginPayload } from '@shared/interfaces/request/authen.interface';
import { RegisterResponse } from '@shared/models/authen.model';
import {
  EmailWrapperPipe,
  LinkWrapperPipe,
} from '@shared/pipes/link-wrapper.pipe';
import { StringReplacementPipe } from '@shared/pipes/string-replacement.pipe';
import { FormUtils } from '@shared/utils/form-utils';
import { StringUtils } from '@shared/utils/string-utils';
import { CookieService } from 'ngx-cookie-service';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-form-login',
  templateUrl: './form-login.component.html',
  imports: [
    NgIcon,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    LanguagePathDirective,
    LoginWithLinkedinBtnComponent,
    DebounceClickDirective,
    StringReplacementPipe,
    LinkWrapperPipe,
    EmailWrapperPipe,
    CommonModule,
  ],
  viewProviders: [provideIcons({ matVisibility, matVisibilityOff })],
})
export class FormLoginComponent implements OnInit, FormComponentInterface {
  @Input() email: string;

  @Input() assignToOrgId: number;

  @Input() eventId;

  joiningEvent = input<boolean>(false);

  @Output() loginSuccessful = new EventEmitter<void>();
  @Output() validate2FA = new EventEmitter<unknown>();

  @Output() openForgotPasswordForm = new EventEmitter<Event>();
  @Output() twoFactorFormOpened = new EventEmitter<unknown>();
  @Output() openAccountInactiveInformation = new EventEmitter();

  @Output() stateReset = new EventEmitter<void>();

  usernameMinLength = environment.loginFormLimit.usernameMinLength;
  passwordMinLength = environment.loginFormLimit.passwordMinLength;

  loginForm: UntypedFormGroup;
  passwordDisplayed = false;
  errorMessage: Record<string, string>;
  formErrorKey;

  portalName: string = environment.portalName;
  portalUrl: string = environment.portalUrl;
  userAccountNotActive = false;
  sentEmailAddress: string;
  isSubmitting?: boolean;

  constructor(
    protected readonly fb: UntypedFormBuilder,
    readonly authenService: AuthenService,
    readonly formErrorService: FormErrorService,
    protected readonly sessionService: SessionService,
    readonly cookieService: CookieService,
    private readonly eventPayment: EventPaymentService
  ) {
    this.portalName = this.cookieService.get(SessionKeyEnum.TENANT_NAME);
    this.portalUrl = this.cookieService.get(SessionKeyEnum.TENANT_DOMAIN);
  }

  getForm(): UntypedFormGroup {
    return this.loginForm;
  }

  ngOnInit(): void {
    this.loginForm = this.fb.group({
      username: [
        '',
        {
          validators: [Validators.required],
        },
      ],
      password: [
        '',
        {
          validators: [Validators.required],
        },
      ],
      rememberMe: [false],
    });

    if (this.email) {
      this.loginForm.get('username')?.setValue(this.email);
    }

    this.formErrorService.register(this);
  }

  onSubmit(): void {
    this.isSubmitting = true;

    this.loginForm.value.username = this.loginForm.value.username.trim();

    const loginPayload: LoginPayload = {
      ...this.loginForm.value,
    };

    const token = StringUtils.getParamFromUrl(UrlParam.Token);
    const eventDateTime = StringUtils.getParamFromUrl(UrlParam.EventDateTime);
    const withPayment = StringUtils.getParamFromUrl('with-payment');
    const withQueryForm = StringUtils.getParamFromUrl('with-query-form');

    if (token) {
      loginPayload.invitedToken = token;
    }

    if (this.assignToOrgId) {
      loginPayload.orgId = this.assignToOrgId;
    }

    if (this.eventId && withPayment) {
      delete loginPayload.invitedToken;
    }

    this.authenService
      .login(loginPayload)
      .pipe(
        finalize(() => {
          this.isSubmitting = false;
        })
      )
      .subscribe((data: unknown) => {
        if (this.sessionService.authenticated) {
          if (data?.['mfa'] && data?.['token']) {
            this.validate2FA.next(data);
            this.twoFactorFormOpened.emit(data);
            return;
          }

          this.sessionService.deleteGlobalFilterCookies();

          if (this.eventId && withPayment) {
            this.eventPayment.handleEventPayment({
              id: this.eventId,
              eventDateTime,
            } as EventInterface);
          } else if (this.eventId && withQueryForm) {
            this.goToEventDetail(this.eventId);
          } else {
            this.loginSuccessful.next();
          }
        } else if (this.handleNotActiveAccount(data as HttpErrorResponse)) {
          return;
        } else {
          this.checkError(data as HttpErrorResponse);
        }
      });
  }

  private goToEventDetail(eventId: number): void {
    FormUtils.navigateTo(
      this.sessionService.appendLanguagePath(
        `${environment.jipUrl.event}/${eventId}?${UrlParam.RegisterEvent}=true`
      )
    );
  }

  handleNotActiveAccount(errorResponse: HttpErrorResponse): boolean {
    // Replace condition by status code
    if (
      errorResponse?.status === 400 &&
      errorResponse.error.exceptionType === 'NotActivatedException'
    ) {
      this.userAccountNotActive = true;

      const username = this.username.value;
      this.authenService.getAccountInfo(username).subscribe({
        next: (data: unknown) => {
          const error = data as HttpErrorResponse;
          if (error?.status === 400) {
            this.checkError(error);
          } else {
            const resData = data as RegisterResponse;
            this.sentEmailAddress = resData.email;
            this.openAccountInactiveInformation.emit();
          }
        },
        error: error => {
          this.checkError(error);
        },
      });

      return true;
    }

    return false;
  }

  resendConfirmationEmail(usernameOrEmail: string): void {
    this.isSubmitting = true;

    const sentToEmail = usernameOrEmail?.trim();
    this.authenService.resendConfirmationEmail(sentToEmail).subscribe({
      next: (res: unknown) => {
        this.isSubmitting = false;

        const error = res as HttpErrorResponse;

        if (error?.status === 400) {
          this.checkError(error);
        }
      },
      error: error => {
        this.isSubmitting = false;
        this.checkError(error);
      },
    });
  }

  checkError(data: HttpErrorResponse): void {
    this.formErrorService.handleError(data);
  }

  togglePassword(event: Event): void {
    event.preventDefault();
    this.passwordDisplayed = !this.passwordDisplayed;
  }

  toggleForgotPasswordForm(event: Event): void {
    event.preventDefault();
    this.openForgotPasswordForm.emit(event);
  }

  get username(): AbstractControl {
    return this.loginForm.get('username');
  }
  get password(): AbstractControl {
    return this.loginForm.get('password');
  }

  get rememberMe(): AbstractControl {
    return this.loginForm.get('rememberMe');
  }

  get linkedinRedirectParams(): { key: UrlParam; value: string | number }[] {
    return this.rememberMe.value
      ? [{ key: UrlParam.RememberMe, value: 'true' }]
      : [];
  }
}
